import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { currencyOperations, type CurrencyOperation } from '@/api/currencyOperations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'documents.currency_operations.create';

const TYPE_LABEL: Record<string, string> = {
  PURCHASE: 'Купівля валюти',
  SALE: 'Продаж валюти',
};

export default function CurrencyOperationsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );
  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['currency-operations', orgId],
    queryFn: () => currencyOperations.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<CurrencyOperation>[]>(
    () => [
      { key: 'document_number', header: 'Номер', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : ''),
      },
      {
        key: 'operation_type', header: 'Тип', width: 160,
        sortValue: (r) => TYPE_LABEL[r.operation_type] ?? r.operation_type,
        cell: (r) => TYPE_LABEL[r.operation_type] ?? r.operation_type,
      },
      { key: 'currency_amount', header: 'Валюта', width: 130, align: 'right', sortValue: (r) => Number(r.currency_amount), cell: (r) => r.currency_amount },
      { key: 'commercial_rate', header: 'Курс комерц.', width: 130, align: 'right', sortValue: (r) => Number(r.commercial_rate), cell: (r) => r.commercial_rate },
      { key: 'nbu_rate', header: 'Курс НБУ', width: 130, align: 'right', sortValue: (r) => Number(r.nbu_rate), cell: (r) => r.nbu_rate },
      { key: 'fx_difference', header: 'FX різн.', width: 130, align: 'right', sortValue: (r) => Number(r.fx_difference), cell: (r) => r.fx_difference },
      { key: 'posted', header: 'Пров.', width: 80, align: 'center', sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓' : '—') },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Валютні операції</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          onClick={() => navigate(`/banking/currency-operations/new?org=${orgId}`)}
        >
          + Валютна операція
        </button>
      </div>
      <DataTable<CurrencyOperation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/banking/currency-operations/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
